<script setup lang="ts">
import {computed, onBeforeUnmount, onMounted, ref, watch} from 'vue';
import {echarts} from '@/utils/common/echartsSetup';
import type {EnhancedCardSparkline} from '@/components/dashboardWidgets/enhancedMetricsCardTypes';
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import {useThemeStore} from "@/stores/ThemeDataStore.ts";

const props = defineProps<{
  config: EnhancedCardSparkline;
}>();

const containerRef = ref<HTMLDivElement>();
let instance: echarts.ECharts | undefined;

const heightStyle = computed(() => {
  const h = props.config.height ?? 40;
  return typeof h === 'number' ? `${h}px` : h;
});

function buildOption() {
  const data = props.config.data ?? [];
  const type = props.config.type ?? 'line';
  const primary = useNamaFlagsStore().darkMode ? useThemeStore().getPrimaryDarkColor() as string : useThemeStore().getPrimaryColor() as string;
  const color = props.config.color ?? primary;
  const smooth = props.config.smooth ?? false;
  const isArea = type === 'area';
  const isColumn = type === 'column';

  return {
    grid: {left: 0, right: 0, top: 2, bottom: 2, containLabel: false},
    xAxis: {type: 'category', show: false, data: data.map((_, i) => i)},
    yAxis: {type: 'value', show: false, scale: true},
    tooltip: {trigger: 'axis', confine: true, formatter: (params: any) => {
        const p = Array.isArray(params) ? params[0] : params;
        return `${p?.value ?? ''}`;
      }},
    series: [{
      type: isColumn ? 'bar' : 'line',
      data,
      smooth,
      symbol: 'none',
      lineStyle: isColumn ? undefined : {width: 1.5, color},
      itemStyle: {color},
      areaStyle: isArea ? {color, opacity: 0.2} : undefined,
      barWidth: isColumn ? '60%' : undefined,
    }],
  } as any;
}

function syncOption() {
  if (!instance) return;
  instance.setOption(buildOption(), {notMerge: true});
}

function ensureInstance() {
  if (!containerRef.value) return;
  if (instance) return;
  instance = echarts.init(containerRef.value, undefined, {renderer: 'canvas'});
  syncOption();
}

const resizeObserver = typeof ResizeObserver !== 'undefined'
  ? new ResizeObserver(() => instance?.resize())
  : undefined;

onMounted(() => {
  ensureInstance();
  if (resizeObserver && containerRef.value) resizeObserver.observe(containerRef.value);
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  instance?.dispose();
  instance = undefined;
});

const themeStore = useThemeStore();
const namaFlags = useNamaFlagsStore();
watch(() => [props.config, themeStore.theme, namaFlags.darkMode], () => syncOption(), {deep: true});
</script>

<template>
  <div ref="containerRef"
       class="tw-w-full"
       :style="{height: heightStyle}"/>
</template>
